import React from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import MomentumRing from "./MomentumRing";
import FlashOnRoundedIcon from "@mui/icons-material/FlashOnRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";

export default function TodayHeroCard({
  state,
  chip,
  title,
  body,
  partnerContextLine = "",
  supportingLine = "",
  ring,
  primaryAction,
  secondaryAction,
}) {
  return (
    <Box
      sx={{
        borderRadius: { xs: "22px", md: "26px" },
        border: "1px solid rgba(255,255,255,0.08)",
        background: "linear-gradient(135deg, #07192e 0%, #0e2b4b 58%, #163f59 100%)",
        color: "#f8fbff",
        px: { xs: 2, md: 2.4 },
        py: { xs: 2, md: 2.35 },
        mb: 1.15,
        boxShadow: "0 18px 36px rgba(10, 18, 33, 0.18)",
        overflow: "hidden",
        position: "relative",
        "&:before": {
          content: '""',
          position: "absolute",
          right: -80,
          top: -90,
          width: 220,
          height: 220,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.07)",
        },
        "&:after": {
          content: '""',
          position: "absolute",
          left: -70,
          bottom: -120,
          width: 220,
          height: 220,
          borderRadius: "50%",
          background: "rgba(55,194,131,0.13)",
        },
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={{ xs: 1.8, lg: 2.1 }}
        alignItems={{ xs: "flex-start", lg: "center" }}
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Box
          sx={{
            width: { xs: "100%", lg: 128 },
            flexShrink: 0,
            display: "grid",
            gridTemplateColumns: { xs: "82px minmax(0, 1fr)", lg: "1fr" },
            alignItems: "center",
            justifyItems: { xs: "start", lg: "center" },
            columnGap: 1.2,
            rowGap: 0.85,
          }}
        >
          <MomentumRing
            value={ring.value}
            total={ring.total}
            label={ring.label}
            state={state}
            updatedToday={Boolean(ring.updatedToday)}
          />
          <Box sx={{ minWidth: 0, textAlign: { xs: "left", lg: "center" } }}>
            <Typography
              sx={{
                color: "rgba(248,251,255,0.72)",
                fontSize: "0.7rem",
                lineHeight: 1.15,
                fontWeight: 850,
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              Momentum
            </Typography>
            <Typography
              sx={{
                mt: 0.2,
                color: "#ffffff",
                fontSize: { xs: "0.92rem", md: "0.98rem" },
                lineHeight: 1.2,
                fontWeight: 850,
              }}
              noWrap
            >
              {ring.label || `of ${ring.total || 7} days`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Chip
            label={chip}
            size="small"
            sx={{
              mb: 0.95,
              backgroundColor: "rgba(55,194,131,0.16)",
              color: "#dffbea",
              fontWeight: 850,
              borderRadius: 999,
              border: "1px solid rgba(55,194,131,0.28)",
              height: 27,
              fontSize: "0.76rem",
              ".MuiChip-label": {
                px: 1.15,
              },
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: 0,
              maxWidth: 620,
              color: "#ffffff",
              fontSize: { xs: "1.58rem", md: "1.9rem" },
              lineHeight: 1.08,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 0.65,
              color: "rgba(248,251,255,0.82)",
              maxWidth: 620,
              lineHeight: 1.42,
              fontSize: { xs: "0.96rem", md: "1rem" },
            }}
          >
            {body}
          </Typography>
          {partnerContextLine ? (
            <Typography
              variant="body2"
              sx={{
                mt: 0.95,
                color: "rgba(248,251,255,0.84)",
                fontWeight: 750,
                maxWidth: 520,
              }}
            >
              {partnerContextLine}
            </Typography>
          ) : null}
          {supportingLine ? (
            <Typography
              variant="body2"
              sx={{
                mt: 0.55,
                color: "rgba(248,251,255,0.68)",
                maxWidth: 520,
              }}
            >
              {supportingLine}
            </Typography>
          ) : null}
          <Stack
            direction="row"
            spacing={0.85}
            useFlexGap
            sx={{ mt: 1.45, flexWrap: "wrap", rowGap: 0.85 }}
          >
            <Button
              variant="contained"
              onClick={primaryAction.onClick}
              startIcon={<FlashOnRoundedIcon />}
              sx={{
                textTransform: "none",
                borderRadius: 999,
                px: 1.7,
                py: 0.85,
                fontWeight: 850,
                maxWidth: "100%",
                backgroundColor: "#ffffff",
                color: "#0d2742",
                boxShadow: "0 10px 20px rgba(0,0,0,0.16)",
                "&:hover": { backgroundColor: "#eef6ff", boxShadow: "0 10px 20px rgba(0,0,0,0.16)" },
                "& .MuiButton-startIcon": { color: "#1f8a57" },
              }}
            >
              {primaryAction.label}
            </Button>
            {secondaryAction ? (
              <Button
                variant="outlined"
                onClick={secondaryAction.onClick}
                startIcon={<ChatBubbleOutlineRoundedIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  px: 1.55,
                  py: 0.82,
                  fontWeight: 850,
                  maxWidth: "100%",
                  borderColor: "rgba(255,255,255,0.34)",
                  backgroundColor: "rgba(255,255,255,0.08)",
                  color: "#ffffff",
                  boxShadow: "none",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.46)",
                    backgroundColor: "rgba(255,255,255,0.13)",
                  },
                }}
              >
                {secondaryAction.label}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
